import { useInfiniteQuery } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React from 'react';

import { TableBody, TableHeader, TableProvider } from 'DesignSystem/features/TableContext';
import { Table, TableCell, TableRow } from 'DesignSystem/ui/table';
import { prefCardCasesInfiniteQuery } from 'pages/preference-cards/api';
import { ClinicalProcedure, PrefCardCase } from 'pages/preference-cards/types';
import { formatDate } from 'utils/helpers';

export function CardCasesTable({ cardId }: { cardId: string }) {
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery(
    prefCardCasesInfiniteQuery(cardId)
  );
  const cases = React.useMemo(() => data?.pages?.flatMap((page) => page.items) ?? [], [data]);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (!containerRefElement) return;
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (scrollHeight - scrollTop - clientHeight < 200 && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  React.useEffect(
    function fetchMoreOnScroll() {
      const containerElement = tableContainerRef.current;
      if (!containerElement) return;

      fetchMoreOnBottomReached(containerElement);
      const handleScroll = () => fetchMoreOnBottomReached(containerElement);
      containerElement.addEventListener('scroll', handleScroll);
      return () => containerElement.removeEventListener('scroll', handleScroll);
    },
    [fetchMoreOnBottomReached]
  );

  const table = useReactTable({
    data: cases,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.case_id,
  });

  return (
    <div className="w-full flex-1 overflow-y-auto">
      <TableProvider table={table} isLoading={isLoading}>
        <Table containerClassName="max-h-[500px]" containerRef={tableContainerRef}>
          <caption className="sr-only">Cases that used this preference card in the last 12 months</caption>
          <TableHeader />
          <TableBody
            renderRow={(row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="px-3">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            )}
          />
        </Table>
      </TableProvider>
    </div>
  );
}

const columnHelper = createColumnHelper<PrefCardCase>();
const columns: ColumnDef<PrefCardCase, any>[] = [
  columnHelper.accessor('case_date', {
    header: 'Procedure Date',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatDate(value, '2-digit', '2-digit', '2-digit') : '–';
    },
  }),
  columnHelper.accessor('client_case_id', { header: 'ID' }),
  {
    id: 'procedures',
    header: 'Procedure',
    cell: ({ row }) => <CaseProcedures caseItem={row.original} />,
  },
  columnHelper.accessor('first_physician_name', {
    header: 'Primary Physician',
    cell: ({ getValue }) => getValue() ?? '–',
  }),
  columnHelper.accessor('location_name', {
    header: 'Approved OR Location',
    cell: ({ getValue }) => getValue() ?? '–',
  }),
];

function caseProcedures(caseItem: PrefCardCase): ClinicalProcedure[] {
  if (caseItem.clinical_procedures?.length) return caseItem.clinical_procedures;
  if (!caseItem.first_clinical_procedure_code) return [];
  return [
    {
      clinical_procedure_code: caseItem.first_clinical_procedure_code,
      description: caseItem.first_clinical_procedure_description ?? '',
    },
  ];
}

function CaseProcedures({ caseItem }: { caseItem: PrefCardCase }) {
  const procedures = caseProcedures(caseItem);
  if (!procedures.length) return <>–</>;

  return (
    <div className="flex flex-col gap-1">
      {procedures.map((procedure) => (
        <div key={procedure.clinical_procedure_code} className="flex flex-col">
          <span>{procedure.description || '–'}</span>
          <span className="type-body-2xs font-normal text-zinc-400">{procedure.clinical_procedure_code}</span>
        </div>
      ))}
    </div>
  );
}
